import { useState } from 'react'
import { Input } from './ui/input'
import { Button } from './ui/button'
import type { BaseMessage } from '../../shared/types'

export default function ContactForm() {
  const [formData, setFormData] = useState<BaseMessage>({
    name: '',
    email: '',
    message: ''
  })
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('loading')
    setErrorMessage('')

    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setStatus('error')
      setErrorMessage('All fields are required')
      return
    }

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })
      
      const data = await response.json()
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message')
      }

      setStatus('success')
      setFormData({ name: '', email: '', message: '' })
    } catch (error) {
      console.error('Error:', error)
      setStatus('error')
      setErrorMessage(error instanceof Error ? error.message : 'Failed to send message')
    }
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-lg mx-auto bg-zinc-800/50 border border-white/10 p-6 rounded-2xl">
      {status === 'success' && (
        <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded text-green-500">
          Message sent successfully!
        </div>
      )}
      {status === 'error' && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded text-red-500">
          {errorMessage}
        </div>
      )}
      <div className="space-y-4">
        <div>
          <label className="block text-white/80 mb-2">Name</label>
          <Input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="bg-white/10 border-white/10 text-white"
            required
            minLength={2}
            maxLength={100}
          />
        </div>
        <div>
          <label className="block text-white/80 mb-2">Email</label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="bg-white/10 border-white/10 text-white"
            required
            pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
          />
        </div>
        <div>
          <label className="block text-white/80 mb-2">Message</label>
          <textarea
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            className="w-full px-3 py-2 bg-white/10 border border-white/10 rounded-md text-white"
            rows={4}
            required
            minLength={10}
            maxLength={1000}
          />
        </div>
        <Button 
          type="submit" 
          disabled={status === 'loading'}
          className="w-full bg-gradient-to-r from-orange-500 via-purple-600 to-pink-500 text-white py-2 rounded hover:bg-orange-600 disabled:opacity-50"
        >
          {status === 'loading' ? 'Sending...' : 'Send Message'}
        </Button>
      </div>
    </form>
  )
} 